@if (pageReady) {
  <main class="gs-page">
    <div class="login-page">
      @if (showAppBanner) {
        <app-mobile-app-banner></app-mobile-app-banner>
      }
      <div
        class="gradient-container login-section"
        [style]="'background:url(' + pageLabels.gradientBackgroundUrl + ')'"
      >
        <div class="login-form-container">
          <img [src]="pageLabels.gasSouthLogoLg" alt="Gas South 20 Years Logo" class="logo-image" />
          <app-notification [pageName]="pageName"></app-notification>
          @if (showNewPasswordCreatedHeader) {
            <h1 class="login-header">{{ pageLabels.newPasswordCreatedHeader }}</h1>
          }
          @if (showAccountDeletedHeader) {
            <h1 class="login-header">{{ pageLabels.oasAccountDeletedHeader }}</h1>
          }
          <form
            [formGroup]="loginForm"
            class="login-form"
            data-cy="form-login"
            aria-label="Login to your Gas South account"
          >
            <mat-form-field class="gs-form-field email-field" appearance="outline">
              <mat-label id="emailLabel">{{ pageLabels.usernameEmailLabel }}</mat-label>
              <input
                matInput
                class="custom-input"
                formControlName="email"
                id="email"
                data-cy="input-login-email"
                aria-labelledby="emailLabel emailError"
              />
              @if (loginForm.get("email")?.hasError("required")) {
                <mat-error id="emailError" data-cy="error-login-email-required">{{
                  pageLabels.usernameEmailErrorText
                }}</mat-error>
              }
            </mat-form-field>
            <div class="gs-form-field remember-me-container">
              <mat-checkbox
                id="RememberMe"
                color="primary"
                formControlName="rememberMe"
                data-cy="checkbox-login-remember-me"
                [attr.aria-describedby]="loginForm.get('biometrics')?.value ? 'rememberMeNote' : null"
              >
                {{ pageLabels.rememberMeCbLabel }}
              </mat-checkbox>
              @if (loginForm.get("biometrics")?.value) {
                <mat-hint id="rememberMeNote" class="remember-me-hint">
                  {{ pageLabels.bioRememberMeHint }}
                </mat-hint>
              }
            </div>
            <div aria-live="polite" class="visually-hidden">
              @if (loginForm.get("biometrics")?.value) {
                {{ pageLabels.bioScreenReaderRememberMeMessage }}
              }
            </div>
            <mat-form-field class="password-container" appearance="outline">
              <mat-label id="passwordLabel">{{ pageLabels.passwordLabel }}</mat-label>
              <input
                matInput
                class="custom-input"
                [type]="passwordVisible ? 'text' : 'password'"
                formControlName="password"
                id="LoginPassword"
                data-cy="input-login-password"
                data-private
                aria-labelledby="passwordLabel passwordError"
              />
              <button
                type="button"
                mat-icon-button
                matSuffix
                (click)="togglePasswordVisibility()"
                [attr.aria-label]="
                  passwordVisible ? pageLabels.hidePasswordAriaLabel : pageLabels.showPasswordAriaLabel
                "
              >
                <i
                  class="fa-solid fa-eye"
                  [class.fa-eye]="!passwordVisible"
                  [class.fa-eye-slash]="passwordVisible"
                  [class.eye-icon]="true"
                ></i>
              </button>
              @if (loginForm.get("password")?.hasError("required")) {
                <mat-error id="passwordError" data-cy="error-login-password-required">{{
                  pageLabels.passwordErrorText
                }}</mat-error>
              } @else if (loginForm.get("password")?.hasError("minlength")) {
                <mat-error id="pwMinLengthErr" data-cy="error-login-password-minlength">{{
                  pageLabels.passwordMinLengthError
                }}</mat-error>
              } @else if (loginForm.get("password")?.hasError("maxlength")) {
                <mat-error id="pwMaxLengthErr" data-cy="error-login-password-maxlength">{{
                  pageLabels.passwordMaxLengthError
                }}</mat-error>
              }
            </mat-form-field>
            @if (showBiometricOption) {
              @if (!encryptedCreds) {
                <div class="gs-form-field biometrics-container">
                  <mat-checkbox
                    id="biometrics"
                    color="primary"
                    formControlName="biometrics"
                    data-cy="checkbox-login-biometrics"
                  >
                    {{ pageLabels.biometricsCbLabel }}
                  </mat-checkbox>
                </div>
              } @else {
                <div class="bio-login-container">
                  <button
                    class="gs-button-link"
                    (click)="onSubmit(true)"
                    data-cy="btn-login-biometrics"
                    [attr.aria-label]="'Log in using ' + pageLabels.biometricsLogInText"
                  >
                    <span>
                      <i [class]="bioIcon"></i>
                    </span>
                    {{ pageLabels.biometricsLogInText }}
                  </button>
                </div>
              }
            }
            <button
              id="btnLogin"
              (click)="onSubmit()"
              mat-flat-button
              class="gs-form-button-primary login-button"
              color="primary"
              data-cy="btn-login-submit"
            >
              {{ pageLabels.loginButtonText }}
            </button>
          </form>
          <a
            class="forgot-account"
            [routerLink]="pageLabels.forgotUsernamePasswordLinkUrl"
            data-cy="link-login-forgot-password"
            >{{ pageLabels.forgotUsernamePasswordText }}</a
          >
          <p class="register-text">{{ pageLabels.registerText }}</p>
          <a class="create-account" data-cy="link-login-register" [routerLink]="pageLabels.registerLinkUrl">{{
            pageLabels.registerLinkText
          }}</a>
        </div>
      </div>
      <div class="account-benefits login-section">
        <gs-icon-list [ListData]="pageLabels.accountBenefitsList" (DebugData)="logDebug($event)"></gs-icon-list>
      </div>
    </div>
  </main>
}

<ng-template #updateAppDialog>
  <h2 mat-dialog-title data-cy="modal-update-app">{{ pageLabels.appUpdateDialogHeader }}</h2>
  <mat-dialog-content>
    <p>{{ pageLabels.appUpdateDialogText }}</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    @if (!updateMandatory) {
      <button class="gs-form-button-primary cancel-button" mat-button mat-dialog-close data-cy="btn-update-continue">
        {{ pageLabels.appUpdateDialogCancel }}
      </button>
    }
    <button
      class="gs-form-button-primary"
      mat-flat-button
      color="primary"
      (click)="openAppStore()"
      data-cy="btn-update-app"
    >
      {{ pageLabels.appUpdateDialogConfirm }}
    </button>
  </mat-dialog-actions>
</ng-template>
